import { useGsapStagger } from '../../hooks/useGsapReveal'
import { useSection } from '../../cms/cms-context'
import { cmsIcon } from '../../cms/icons'

/* The four cards that used to close the Process section.
 *
 * Split into a section of its own, and only because the console needs it to
 * be: a band that cannot be moved or switched off separately from the steps
 * above it is not really a section. It still renders immediately under the
 * steps in the order the page ships in, with the same spacing it always had —
 * the mt-24 the list carried inside Process is the py here.
 *
 * The icons were inline SVG paths before, which the console has no way to
 * offer a person. They are named Lucide icons now, chosen to match: the
 * rising line, the receipt, the star and the two figures. */
const ADVANTAGES = [
  {
    id: 'markup',
    content: {
      icon: 'TrendingUp',
      title: 'Lowest markup, in writing',
      body: 'We negotiate rate, not just price. If a bank quotes you lower, we match it or tell you to take it.',
    },
  },
  {
    id: 'pricing',
    content: {
      icon: 'ReceiptText',
      title: 'Zero-surprise pricing',
      body: 'Insurance, tracker, registration and processing itemised up front. No line item appears at signing that was not in your quote.',
    },
  },
  {
    id: 'new',
    content: {
      icon: 'Star',
      title: 'Brand new only',
      body: 'Every unit is factory-fresh with full warranty and dealership PDI. We do not trade used stock, so there is nothing to hide.',
    },
  },
  {
    id: 'contact',
    content: {
      icon: 'Users',
      title: 'One point of contact',
      body: 'The person who quotes you is the person who delivers your car. No call-centre handoffs and no chasing a file between departments.',
    },
  },
]

function Advantages() {
  const cms = useSection()
  const items = cms.items(ADVANTAGES)

  const ref = useGsapStagger<HTMLUListElement>('[data-advantage]', {
    y: 26,
    stagger: 0.08,
  })

  return (
    <section className="pb-24 sm:pb-32">
      <div className="mx-auto max-w-[1320px] px-5 sm:px-8">
        <ul ref={ref} className="grid grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-4">
          {items.map((item) => {
            const Icon = cmsIcon(item.content.icon)
            return (
              <li
                key={item.id}
                data-advantage
                {...item.props}
                className="group rounded-2xl border border-line bg-raised p-7 shadow-card transition duration-300 hover:-translate-y-1 hover:border-lime/30 hover:shadow-lift"
              >
                <span className="icon-ring size-12 transition duration-300 group-hover:bg-lime group-hover:text-void">
                  <Icon aria-hidden="true" strokeWidth={1.5} className="size-5" />
                </span>
                <h3 className="mt-6 font-display text-[17px] font-semibold tracking-[-0.015em]">
                  {item.content.title}
                </h3>
                <p className="mt-2.5 text-[14px] leading-relaxed text-steel">{item.content.body}</p>
              </li>
            )
          })}
        </ul>
      </div>
    </section>
  )
}

export default Advantages
